import { CRM_TODAY_BRANDS_MAX } from "@nexxt/contracts/crm";
import * as z from "zod";

// The lens of the brand (ADR-306): "All brands", "My brands" (the crews the user is in) or one brand, over Today, the
// campaigns, the emails and the brands. A convenience, never a permission (B-168): crm gives every brand to every
// reader. In the URL (`brand=`) so a link shows the same, and remembered in this browser for the next visit. The rules
// here are pure; the hook is in use-brand-lens.ts.

/** What the URL and the browser hold: `all`, `mine` or the id of a brand. */
export const lensValue = z.union([z.enum(["all", "mine"]), z.uuid()]);
export type LensValue = z.output<typeof lensValue>;

export type BrandLens = { kind: "all" } | { kind: "mine" } | { kind: "brand"; brandOrgId: string };

export interface LensBrand {
  id: string;
  mine: boolean;
}

export function lensOf(value: LensValue): BrandLens {
  if (value === "all" || value === "mine") return { kind: value };
  return { kind: "brand", brandOrgId: value };
}

/**
 * The lens in force: the URL's, else the last one chosen in this browser, else "My brands" for a person in the crew
 * of a brand and "All brands" for the others (Sales and the area managers work by territory).
 */
export function chosenLens(url: LensValue | undefined, stored: LensValue | undefined, brands: readonly LensBrand[]) {
  return lensOf(url ?? stored ?? (brands.some((b) => b.mine) ? "mine" : "all"));
}

export const lensValueOf = (lens: BrandLens): LensValue => (lens.kind === "brand" ? lens.brandOrgId : lens.kind);

/**
 * The brands a read of crm is narrowed to, undefined for all of them. "My brands" of a person in no crew is none at
 * all, an empty list; past the cap of Today the first by name (the list is by name), which a person of so many crews
 * reads as "All brands" anyway.
 */
export function lensBrandIds(lens: BrandLens, brands: readonly LensBrand[]): string[] | undefined {
  if (lens.kind === "all") return undefined;
  if (lens.kind === "brand") return [lens.brandOrgId];
  return brands
    .filter((b) => b.mine)
    .map((b) => b.id)
    .slice(0, CRM_TODAY_BRANDS_MAX);
}

/**
 * Whether something of a brand is in view: an email or a kit. Tomorrow's own (no brand) are everyone's, so "My brands"
 * keeps them; one brand shows its own alone.
 */
export function inLens(lens: BrandLens, brandOrgId: string | null, brands: readonly LensBrand[]): boolean {
  if (lens.kind === "all") return true;
  if (lens.kind === "brand") return brandOrgId === lens.brandOrgId;
  return brandOrgId === null || brands.some((b) => b.id === brandOrgId && b.mine);
}

/** The choice of this browser, as stored: nothing when it holds something else. */
export function parseStoredLens(raw: string | null): LensValue | undefined {
  const parsed = lensValue.safeParse(raw);
  return parsed.success ? parsed.data : undefined;
}
